import { useModuleConnectors } from "../../skills/hooks/useModuleConnectors";
import { getConnectorPanel } from "../../integrations/connectorRegistry";
import { ConnectorTabs } from "./ConnectorTabs";
import type { StudioModuleConnector } from "../../services/studio/types";
import type { ComponentType } from "react";

interface ConnectorModuleShellProps {
  readonly moduleId: string;
  readonly onBack?: () => void;
  readonly onOpenSettings?: () => void;
  readonly userEmail?: string;
  /**
   * Fallback component rendered when Studio settings haven't loaded yet
   * (no module data) or when no panel is registered for the connector's
   * systemName. Preserves the legacy single-connector behaviour while the
   * settings response is in-flight.
   */
  readonly fallback?: ComponentType<{
    onBack?: () => void;
    onOpenSettings?: () => void;
    userEmail?: string;
  }>;
}

/**
 * Renders a module's connector panels. When the module (looked up by id)
 * has 2+ linked connectors, a tab strip is rendered with one tab per
 * connector — sequenced as the settings endpoint returns them. With 1
 * connector the panel renders directly. While settings load, the fallback
 * component is rendered.
 */
export function ConnectorModuleShell({
  moduleId,
  onBack,
  onOpenSettings,
  userEmail,
  fallback: Fallback,
}: Readonly<ConnectorModuleShellProps>) {
  const connectors = useModuleConnectors(moduleId);

  if (connectors.length === 0) {
    if (Fallback) {
      return <Fallback onBack={onBack} onOpenSettings={onOpenSettings} userEmail={userEmail} />;
    }
    return null;
  }

  return (
    <ConnectorTabs
      connectors={connectors}
      renderPanel={(c: StudioModuleConnector) => {
        const Panel = getConnectorPanel(c.systemName);
        if (!Panel) {
          if (Fallback) {
            return <Fallback onBack={onBack} onOpenSettings={onOpenSettings} userEmail={userEmail} />;
          }
          return null;
        }
        return <Panel onBack={onBack} onOpenSettings={onOpenSettings} userEmail={userEmail} />;
      }}
    />
  );
}
